import React, { useEffect, useState, useCallback, useRef } from 'react';
import SharingTab from './SharingTab';
import Header from '../../compos/Header/Header';
import ExploreMap from './ExploreMap';
import LocationSearchBar from './LocationSearchBar';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Clock, Gauge, Search, MapPin, ChevronDown, ChevronLeft, ArrowUp } from 'lucide-react';
import Placeholder from '../../Media/Team/placeholder.png';
import DefaultPFP from '../../Media/Icons/DefaultPFP.webp';
import UnknownAuthority from '../../Media/Icons/UnknownAuthority.webp';
import Anonymous from '../../Media/Icons/anonymous.png';
import { BACKEND_URL, WEB_BASE_URL, mediaUrl, profilePicUrl } from '../../config';
import { parseISO } from 'date-fns';
import { formatCustomDistanceToNow } from '../../utils/timeUtils';
import { isHttpUrl } from '../../utils/url';
import { useData } from '../../DataContext';
import RiskoMeter from '../RiskoMeter/RiskoMeter';
import CardDescription from './CardDescription';

// Define MediaType and PostType
type MediaType = {
	format: string;
	internalURL: string;
	latitude: string;
	longitude: string;
	link?: string | null;
};

type PostType = {
	id: number;
	title: string;
	body?: string;
	media: MediaType[];
	isAnonymous: boolean;
	updatedAt: string;
	createdAt: string;
	commentCount: number;
	likeCount: number;
	tags: string;
	isLiked: number;
	shareHash?: string;
	user: { name: string; id: number; profilepicURL: string };
};

export type ExplorePostDataType = {
	id: number;
	shareHash?: string;
	title: string;
	content?: string;
	alteredContent: string;
	isImage: boolean;
	format: string;
	image_url: any;
	media_filename: string;
	name: string;
	location: string;
	time: string;
	commentCount: number;
	likeCount: number;
	tags: string;
	profilepicURL: string;
	isLiked: number;
	webLink: string;
	youtubeLink: string;
	isAnonymous: boolean;
};

//const Placeholder = 'https://via.placeholder.com/400';

const PostPlaceholderSVG = () => (
	<div className="w-full h-full bg-gray-100 overflow-hidden relative flex items-center justify-center animate-shimmer-wave">
		<svg className="w-32 h-32 text-gray-300 z-10 drop-shadow-sm relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	</div>
);

const PostImage = ({ src, alt, className }: { src: string; alt: string; className?: string }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);
	// IntersectionObserver-gated src: top-to-bottom fetch order as user scrolls.
	const [shouldLoad, setShouldLoad] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (shouldLoad) return;
		const el = containerRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setShouldLoad(true);
					observer.disconnect();
				}
			},
			{ rootMargin: '300px 0px' }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [shouldLoad]);

	if (hasError)
		return (
			<div className={`${className || ''} aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100`}>
				<PostPlaceholderSVG />
			</div>
		);

	return (
		<div
			ref={containerRef}
			className={`relative aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100 ${className || ''}`}
		>
			{!isLoaded && (
				<div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
					<PostPlaceholderSVG />
				</div>
			)}
			{shouldLoad && (
				<img
					src={src}
					alt={alt}
					decoding='async'
					className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
					onLoad={() => setIsLoaded(true)}
					onError={() => setHasError(true)}
				/>
			)}
		</div>
	);
};

const ProfileImage = ({
	name,
	pfpURL,
	className,
	isAnonymous,
}: {
	name: string;
	pfpURL?: string | null;
	className?: string;
	isAnonymous?: boolean;
}) => {
	const isAuth = name.toLowerCase().includes('authority');
	const [hasError, setHasError] = useState(false);

	const src = isAnonymous
		? Anonymous
		: !hasError && pfpURL && pfpURL !== 'null' && pfpURL !== ''
			? profilePicUrl(pfpURL)
			: isAuth
				? UnknownAuthority
				: DefaultPFP;

	return <img src={src} alt={name} className={className} onError={() => setHasError(true)} />;
};

const Explore = () => {
	const { postMetadata, updatePostMetadata } = useData();
	const [exploreState] = useState(() => {
		const savedStr = sessionStorage.getItem('exploreFeedState');
		if (savedStr) {
			try {
				const parsed = JSON.parse(savedStr);
				sessionStorage.removeItem('exploreFeedState');
				return parsed;
			} catch (e) {
				console.error(e);
			}
		}
		return null;
	});

	const [posts, setPosts] = useState<ExplorePostDataType[]>(exploreState?.posts || []);
	const [originalPosts, setOriginalPosts] = useState<ExplorePostDataType[]>(exploreState?.originalPosts || []);
	const [loading, setLoading] = useState(!exploreState);
	const [radius, setRadius] = useState<number>(exploreState?.radius || 4);
	const [searchQuery, setSearchQuery] = useState<string>(exploreState?.searchQuery || '');
	const [locationSearchQuery, setLocationSearchQuery] = useState<string>(exploreState?.locationSearchQuery || '');
	const [riskoMeterMediaId, setRiskoMeterMediaId] = useState<string | null>(null);
	const [toastMessage, setToastMessage] = useState<string | null>(null);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [isRadiusDropdownOpen, setIsRadiusDropdownOpen] = useState(false);
	const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(exploreState?.isDesktopSidebarOpen ?? true);
	const radiusDropdownRef = useRef<HTMLDivElement>(null);

	// Close dropdown on click outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (radiusDropdownRef.current && !radiusDropdownRef.current.contains(event.target as Node)) {
				setIsRadiusDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Auto-dismiss the "post no longer exists" toast.
	useEffect(() => {
		if (!toastMessage) return;
		const t = setTimeout(() => setToastMessage(null), 3000);
		return () => clearTimeout(t);
	}, [toastMessage]);

	const radiusOptions = [
		{ value: 1, label: '1km' },
		{ value: 2, label: '10km' },
		{ value: 3, label: '100km' },
		{ value: 4, label: '500km' },
	];

	const [coords, setCoords] = useState<{ lat: number; lng: number }>(exploreState?.coords || { lat: 25, lng: 77 });
	const [likes, setLikes] = useState<Record<number, boolean>>({});
	const [page, setPage] = useState<number>(exploreState?.page || 1);
	const [hasMore, setHasMore] = useState<boolean>(exploreState?.hasMore ?? true);
	const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
	const isFirstRender = useRef(true);
	const [showScrollToOpen, setShowScrollToOpen] = useState(false);
	const lastScrollY = useRef(0);
	const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const topRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const checkVisibility = () => {
			const currentScrollY = window.scrollY;

			let isScrollingDown = false;
			let isScrollingUp = false;

			if (currentScrollY > lastScrollY.current) {
				isScrollingDown = true;
			} else if (currentScrollY < lastScrollY.current) {
				isScrollingUp = true;
			}

			lastScrollY.current = currentScrollY;

			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}

			let willShowTop = false;
			if (currentScrollY <= 50) {
				setShowScrollToOpen(false);
			} else if (isScrollingUp) {
				setShowScrollToOpen(true);
				willShowTop = true;
			} else if (isScrollingDown) {
				setShowScrollToOpen(false);
			}

			if (willShowTop) {
				hideTimeoutRef.current = setTimeout(() => {
					setShowScrollToOpen(false);
				}, 2000);
			}
		};

		window.addEventListener('scroll', checkVisibility);
		window.addEventListener('resize', checkVisibility);

		const resizeObserver = new ResizeObserver(checkVisibility);
		resizeObserver.observe(document.body);

		checkVisibility();
		const timer = setTimeout(checkVisibility, 100);

		return () => {
			window.removeEventListener('scroll', checkVisibility);
			window.removeEventListener('resize', checkVisibility);
			resizeObserver.disconnect();
			clearTimeout(timer);
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}
		};
	}, []);
	const navigate = useNavigate();
	const [openShareId, setOpenShareId] = useState<number | null>(null);
	const shareDropdownRef = useRef<HTMLDivElement>(null);
	const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
	const EXPLORE_PAGE_SIZE = 10;
	// Server-side search state (mirrors MyFeed).
	// Search is intersected with the current radius — the server's
	// distanceBasedPosts endpoint applies both filters together.
	const activeSearchRef = useRef<string>(exploreState?.searchQuery || '');
	const requestIdRef = useRef<number>(0);
	const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isFirstSearchEffect = useRef<boolean>(true);

	// Scroll restoration using rAF polling.
	// IMPORTANT: removeItem is deferred inside tryScroll so that a mid-loop
	// data update (re-render) doesn't wipe the target before we've reached it.
	useEffect(() => {
		const savedScrollStr = sessionStorage.getItem('exploreScrollPos');
		if (!savedScrollStr) return;
		const target = parseInt(savedScrollStr, 10);
		if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
		let rafId: number;
		let attempts = 0;
		const tryScroll = () => {
			attempts++;
			const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
			if (maxScroll >= target || attempts > 300) {
				// Clear the saved position only after we are actually scrolling to it
				sessionStorage.removeItem('exploreScrollPos');
				window.scrollTo({ top: target, behavior: 'instant' as ScrollBehavior });
				// No further rAF — we're done
			} else {
				rafId = requestAnimationFrame(tryScroll);
			}
		};
		rafId = requestAnimationFrame(tryScroll);
		return () => cancelAnimationFrame(rafId);
	}, [posts]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target as Node)) {
				setOpenShareId(null);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleShareButtonClick = (postId: number) => {
		setOpenShareId(openShareId === postId ? null : postId);
	};

	const fetchExplorePosts = useCallback(async (lat: number, lon: number, level: number, targetPage: number = 1) => {
		if (targetPage === 1) setLoading(true);
		else setIsFetchingMore(true);

		const myReqId = ++requestIdRef.current;
		const search = activeSearchRef.current;

		try {
			const baseUrl = BACKEND_URL;
			const endpoint = 'api/distanceBasedPosts';

			const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
			const url = `${baseUrl}/${endpoint}?lat=${lat}&lon=${lon}&level=${level}&limit=${EXPLORE_PAGE_SIZE}&offset=${(targetPage - 1) * EXPLORE_PAGE_SIZE}${searchParam}`;

			const response = await fetch(url, {
				credentials: 'include',
				headers: {
				},
			});

			if (!response.ok) {
				const errorText = await response.text();
				console.error('Server Error Response:', errorText);
				throw new Error(`HTTP ${response.status}`);
			}

			const data = await response.json();

			// Drop stale responses — a newer fetch has superseded this one
			// (e.g. the user kept typing in the search box).
			if (myReqId !== requestIdRef.current) return;

			// PostApi.dart returns response.data directly, which is a List of posts
			const result: PostType[] = Array.isArray(data) ? data : [];

			if (result.length < EXPLORE_PAGE_SIZE) {
				setHasMore(false);
			} else {
				setHasMore(true);
			}

			const processedPosts = result.map((each) => {
				let isImage = false;
				let internalURL = '';
				let mediaFormat = 'unknown';
				let webLink = '';
				let youtubeLink = '';

				if (each.media && each.media.length > 0) {
					if (each.media[0].format && each.media[0].format.substring(0, 5) === 'image') {
						isImage = true;
						mediaFormat = 'image';
					} else if (each.media[0].format && each.media[0].format.substring(0, 5) === 'video') {
						mediaFormat = 'video';
					} else {
						mediaFormat = each.media[0].format || 'unknown';
					}
					internalURL = each.media[0].internalURL || '';
					if (internalURL && !internalURL.endsWith('.jpg')) {
						// internalURL += '.jpg'; // removed this hack as serveMedia should handle it now
					}

					if (each.media[0].link) {
						const url = each.media[0].link;
						const regex =
							/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)/;
						const match = url.match(regex);
						// Only accept http(s) links for the <iframe> embed below — the link is
						// attacker-controlled (set by the post author), so javascript:/data:/
						// intent: etc. must never reach the iframe src. Non-web links are dropped.
						if (match === null) webLink = isHttpUrl(url) ? url : '';
						else youtubeLink = match[1];
					}
				}

				const body = each.body || '';
				const truncatedLength = body.length > 65 ? body.slice(0, 65) + '... ' : body;

				const finalImageUrl = isImage && internalURL ? mediaUrl(internalURL) : Placeholder;

				return {
					id: each.id,
					title: each.title,
					content: body,
					alteredContent: truncatedLength,
					isImage: isImage,
					format: mediaFormat,
					image_url: finalImageUrl,
					media_filename: internalURL,
					name: each.isAnonymous ? 'Anonymous' : each.user?.name || 'Unknown',
					location:
						each.media && each.media.length > 0 ? `(${each.media[0].latitude}, ${each.media[0].longitude})` : '',
					time: `${each.createdAt}`,
					commentCount: each.commentCount || 0,
					likeCount: each.likeCount || 0,
					isLiked: each.isLiked || 0,
					tags: each.tags || '',
					profilepicURL: each.user?.profilepicURL || '',
					webLink: webLink,
					youtubeLink: youtubeLink,
					isAnonymous: each.isAnonymous || false,
					shareHash: each.shareHash,
				} as ExplorePostDataType;
			});

			if (targetPage === 1) {
				setPosts(processedPosts);
				setOriginalPosts(processedPosts);
			} else {
				setPosts((prevPosts) => {
					const existingIds = new Set(prevPosts.map((p) => p.id));
					const uniqueNewPosts = processedPosts.filter((p) => !existingIds.has(p.id));
					return [...prevPosts, ...uniqueNewPosts];
				});
				setOriginalPosts((prevOriginal) => {
					const existingIds = new Set(prevOriginal.map((p) => p.id));
					const uniqueNewPosts = processedPosts.filter((p) => !existingIds.has(p.id));
					return [...prevOriginal, ...uniqueNewPosts];
				});
			}

			const initialLikes = processedPosts.reduce(
				(acc, post) => {
					acc[post.id] = post.isLiked === 1;
					return acc;
				},
				{} as Record<number, boolean>
			);
			// Merge: backend data sets base, but user's existing interactions take precedence
			setLikes((prev) => ({ ...initialLikes, ...prev }));

			// Update global metadata registry
			processedPosts.forEach((post) => {
				updatePostMetadata(post.id, {
					likeCount: post.likeCount,
					isLiked: post.isLiked === 1,
					commentCount: post.commentCount,
				});
			});
		} catch (error) {
			console.error('Explore Fetch Error:', error);
			if (targetPage === 1) {
				setPosts([]);
				setOriginalPosts([]);
			}
		} finally {
			setLoading(false);
			setIsFetchingMore(false);
		}
	}, [updatePostMetadata]);

	// 1. On mount: try to acquire user GPS.
	useEffect(() => {
		if (exploreState) {
			return; // We restored state from a previous visit, so keep everything as is!
		}
		sessionStorage.removeItem('exploreScrollPos');
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(pos) =>
					setCoords((prev) => {
						if (prev.lat === pos.coords.latitude && prev.lng === pos.coords.longitude) return prev;
						return { lat: pos.coords.latitude, lng: pos.coords.longitude };
					}),
				() => console.warn('GPS Denied — keeping default location (25, 77)'),
				{ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
			);
		}
	}, [exploreState]);

	// Background Refresh on Restore
	useEffect(() => {
		if (exploreState && posts.length > 0) {
			// Refresh only the first 40 posts or current loaded amount to ensure freshness without excessive load
			const refreshCount = Math.min(posts.length, 100);
			const lat = coords.lat;
			const lon = coords.lng;
			const level = radius;
			const url = `${BACKEND_URL}/api/distanceBasedPosts?lat=${lat}&lon=${lon}&level=${level}&limit=${refreshCount}&offset=0`;

			fetch(url, {
				credentials: 'include',
			})
				.then((res) => res.json())
				.then((data) => {
					if (Array.isArray(data)) {
						data.forEach((each) => {
							updatePostMetadata(each.id, {
								likeCount: each.likeCount,
								isLiked: each.isLiked === 1,
								commentCount: each.commentCount,
							});
						});
					}
				})
				.catch((err) => console.error('Background refresh error:', err));
		}
	}, [exploreState, posts.length, coords.lat, coords.lng, radius, updatePostMetadata]);

	// 2. Fetching Trigger - Pass 'lng' into the 'lon' parameter
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			if (exploreState) {
				// If we restored from state, we ALREADY have the correct posts loaded, so skip fetching!
				// But next time coords or radius change, we DO want to fetch.
				return;
			}
		}
		setLoading(true);
		setPosts([]);
		setOriginalPosts([]);
		setPage(1);
		setHasMore(true);
		fetchExplorePosts(coords.lat, coords.lng, radius, 1);
	}, [coords, radius, fetchExplorePosts, exploreState]);

	const handleLoadMore = () => {
		const nextPage = page + 1;
		setPage(nextPage);
		fetchExplorePosts(coords.lat, coords.lng, radius, nextPage);
	};

	// Infinite scroll via IntersectionObserver.
	// Works for both unfiltered and search-filtered lists — the server
	// paginates the same filtered set so we just keep asking for the next page.
	// posts.length is in the deps so the observer re-binds when the sentinel
	// unmounts/remounts (e.g. list goes 0→N after clearing a search).
	useEffect(() => {
		const sentinel = loadMoreSentinelRef.current;
		if (!sentinel) return;
		if (!hasMore) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry && entry.isIntersecting && !isFetchingMore && !loading) {
					handleLoadMore();
				}
			},
			{ rootMargin: '400px 0px' }
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasMore, isFetchingMore, loading, page, coords, radius, posts.length]);

	const navigateToPost = (postId: string) => {
		sessionStorage.setItem('exploreScrollPos', window.scrollY.toString());
		sessionStorage.setItem(
			'exploreFeedState',
			JSON.stringify({
				posts,
				originalPosts,
				page,
				hasMore,
				coords,
				radius,
				searchQuery,
				locationSearchQuery,
				isDesktopSidebarOpen,
			})
		);
		navigate(`/getpost/${postId}`);
	};

	const reactPost = async (postId: number, type: string, isAnonymous: boolean) => {
		const requestOptions: RequestInit = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ postId, type, isAnonymous }),
			credentials: 'include',
		};
		try {
			const res = await fetch(`${BACKEND_URL}/api/react`, requestOptions);
			// fetch() doesn't throw on 4xx. A 404 means the post was deleted
			// (e.g. from another device) — signal the caller to drop the card.
			if (res.status === 404) return false;
			return true;
		} catch (error) {
			console.error(error);
			return true;
		}
	};

	// A like landed on a post that no longer exists. Tell the user and refresh
	// the feed by removing the dead card (and its cached state) on the spot.
	const handleDeadPost = (postId: number) => {
		setToastMessage('This post no longer exists.');
		setPosts((prev) => prev.filter((p) => p.id !== postId));
		setLikes((prev) => {
			const next = { ...prev };
			delete next[postId];
			return next;
		});
	};

	const handleLikeClick = (postId: number, isAnonymous: boolean) => {
		const currentMetadata = postMetadata[postId];
		const isLiked = currentMetadata?.isLiked ?? likes[postId] ?? posts.find((p) => p.id === postId)?.isLiked === 1;
		const newIsLiked = !isLiked;
		const newType = newIsLiked ? 'like' : 'dislike';

		setLikes((prevLikes) => ({ ...prevLikes, [postId]: newIsLiked }));

		const post = posts.find((p) => p.id === postId);
		const currentCount = currentMetadata?.likeCount ?? post?.likeCount ?? 0;
		const newLikeCount = newIsLiked ? currentCount + 1 : Math.max(0, currentCount - 1);

		updatePostMetadata(postId, { isLiked: newIsLiked, likeCount: newLikeCount });

		setPosts((prevPosts) =>
			prevPosts.map((post) => (post.id === postId ? { ...post, likeCount: newLikeCount } : post))
		);
		reactPost(postId, newType, isAnonymous).then((stillExists) => {
			if (!stillExists) handleDeadPost(postId);
		});
	};

	const renderHighlightedText = (text: any, query: string) => {
		const safeText = text ? String(text) : '';
		const trimmedQuery = query.trim();
		if (!trimmedQuery) return <span>{safeText}</span>;
		try {
			const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const regex = new RegExp(`(${escapedQuery})`, 'gi');
			const parts = safeText.split(regex);
			return (
				<>
					{parts.map((part, i) =>
						part.toLowerCase() === trimmedQuery.toLowerCase() ? (
							<span key={i} className='bg-blue-200 text-blue-800 rounded px-0.5 font-bold'>
								{part}
							</span>
						) : (
							<span key={i}>{part}</span>
						)
					)}
				</>
			);
		} catch (e) {
			return <span>{safeText}</span>;
		}
	};

	// Minimum query length before we hit the server. A leading-wildcard
	// `LIKE '%x%'` on a 1–2 char term can't use an index (full scan) and
	// matches almost everything, so it's the costliest, least useful query.
	// Below this we treat the box as empty and show the normal nearby feed.
	const MIN_SEARCH_LENGTH = 3;

	// Debounced server-side search constrained to the current radius.
	// The backend's distanceBasedPosts endpoint intersects ?search= with
	// the lat/lon/level filter, so results stay within the user's selected
	// region. Pagination then works naturally for the filtered set.
	useEffect(() => {
		// Anything shorter than the minimum counts as "no search".
		const effective = searchQuery.trim().length < MIN_SEARCH_LENGTH ? '' : searchQuery.trim();
		if (isFirstSearchEffect.current) {
			isFirstSearchEffect.current = false;
			activeSearchRef.current = effective;
			return;
		}
		if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		searchDebounceRef.current = setTimeout(() => {
			// Skip the refetch when the effective term hasn't actually changed
			// (e.g. the user is still typing below the threshold).
			if (effective === activeSearchRef.current) return;
			activeSearchRef.current = effective;
			setPosts([]);
			setOriginalPosts([]);
			setPage(1);
			setHasMore(true);
			fetchExplorePosts(coords.lat, coords.lng, radius, 1);
		}, 300);
		return () => {
			if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);

	return (
		<div ref={topRef} className='min-h-screen bg-gray-100'>
			{/* Transient toast — e.g. when a liked post no longer exists */}
			{toastMessage && (
				<div className='fixed top-6 left-1/2 -translate-x-1/2 z-[1100] bg-gray-900 text-white px-5 py-3 rounded-xl text-sm font-semibold shadow-2xl'>
					{toastMessage}
				</div>
			)}

			<Header />

			{/* Mobile Overlay */}
			{isSidebarOpen && (
				<div className='fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden' onClick={() => setIsSidebarOpen(false)} />
			)}

			{/* Mobile Hovering Location Button */}
			{!isSidebarOpen && (
				<div className='fixed bottom-20 left-4 z-40 lg:hidden'>
					<button
						onClick={() => setIsSidebarOpen(true)}
						className='bg-[#004a99]/80 text-white rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center backdrop-blur-sm'
						style={{ width: '40px', height: '40px' }}
					>
						<MapPin className='w-6 h-6' />
					</button>
				</div>
			)}

			{/* Left Sidebar (Desktop & Mobile) */}
			<div
				className={`fixed left-0 top-[var(--header-height,80px)] h-screen bg-white border-r border-gray-200 overflow-y-auto z-50 transition-transform duration-300 w-80 lg:w-96 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${isDesktopSidebarOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'}`}
			>
				<div className='pt-8 px-6 pb-32'>
					<div className='relative mb-2'>
						{' '}
						{/* Header Group */}
						<div className='flex justify-between items-center h-9'>
							<h2 className='text-lg font-bold text-gray-900 relative top-[1px] leading-tight'>Location Filters</h2>
							<div className='flex items-center gap-2'>
								<button
									className='hidden lg:flex p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors inline-flex items-center justify-center relative -top-[2px]'
									onClick={() => setIsDesktopSidebarOpen(false)}
									title='Collapse sidebar'
								>
									<ChevronLeft className='w-5 h-5' />
								</button>
								<button
									className='lg:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-full inline-flex items-center justify-center relative -top-[2px]'
									onClick={() => setIsSidebarOpen(false)}
								>
									<ChevronLeft className='w-5 h-5' />
								</button>
							</div>
						</div>
					</div>
					<LocationSearchBar
						query={locationSearchQuery}
						setQuery={setLocationSearchQuery}
						onSelectLocation={(lat, lng) => setCoords({ lat, lng })}
					/>
					<ExploreMap coords={coords} setCoords={setCoords} radius={radius} posts={posts} />
					<div className='flex items-center justify-start gap-4 mt-4 px-1'>
						<span className='text-sm font-bold text-gray-700 shrink-0'>Radius from pin:</span>
						<div className='relative shrink-0' ref={radiusDropdownRef}>
							<button
								onClick={() => setIsRadiusDropdownOpen(!isRadiusDropdownOpen)}
								className='flex items-center justify-between min-w-[100px] pl-4 pr-3 py-1.5 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 text-sm font-medium transition-all hover:border-blue-300 shadow-sm'
							>
								<span>{radiusOptions.find((opt) => opt.value === radius)?.label || '1km'}</span>
								<ChevronDown
									size={16}
									className={`ml-2 transition-transform duration-200 text-gray-500 ${isRadiusDropdownOpen ? 'rotate-180' : ''}`}
								/>
							</button>

							{isRadiusDropdownOpen && (
								<div className='absolute top-full left-0 mt-2 w-full bg-white border border-gray-100 rounded-2xl shadow-xl z-[70] overflow-hidden'>
									{radiusOptions.map((option) => (
										<button
											key={option.value}
											onClick={() => {
												setRadius(option.value);
												setIsRadiusDropdownOpen(false);
											}}
											className={`w-full text-left px-4 py-2 text-sm transition-colors ${radius === option.value
													? 'bg-blue-50 text-blue-600 font-bold'
													: 'text-gray-700 hover:bg-gray-50'
												}`}
										>
											{option.label}
										</button>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Desktop Expand Button */}
			{!isDesktopSidebarOpen && (
				<div className='fixed top-24 left-6 z-40 hidden lg:block'>
					<button
						onClick={() => setIsDesktopSidebarOpen(true)}
						className='bg-[#004a99] text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center group'
						style={{ width: '48px', height: '48px' }}
						title='Expand sidebar'
					>
						<MapPin className='w-6 h-6' />
						<span className='absolute left-full ml-3 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none'>
							Expand Filters
						</span>
					</button>
				</div>
			)}

			<div className='pt-[var(--header-height,80px)] flex justify-center'>
				<div className='w-full max-w-2xl px-6 py-6'>
					{/* Title Header */}
					<div className='mb-6'>
						<h1 className='m-0 text-2xl font-bold leading-tight text-gray-900 text-left'>
							Explore Nearby
						</h1>
						<p className='m-0 mt-1 leading-tight text-gray-600 text-left'>Find posts in your area</p>
					</div>

					{/* Search Bar */}
					<div className='mb-6'>
						<div className='relative'>
							<Search className='absolute left-[14px] top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
							<input
								type='text'
								className='w-full pl-10 sm:pl-12 pr-14 sm:pr-20 py-1.5 sm:py-2 bg-white border border-gray-200 rounded-full 
                                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                    shadow-sm hover:shadow-md transition-shadow text-gray-900 text-[13px] sm:text-base'
								placeholder='Search by Title or Username'
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
							{searchQuery && (
								<button
									onClick={() => setSearchQuery('')}
									className='absolute right-[6px] top-1/2 -translate-y-1/2 px-3 bg-gray-100 hover:bg-gray-200 text-gray-500 text-[10px] font-bold rounded-full transition-colors border border-gray-200 flex items-center justify-center leading-none'
									style={{ height: 'calc(100% - 12px)' }}
								>
									Clear
								</button>
							)}
						</div>
					</div>

					{/* Posts Feed */}
					<div className='space-y-4'>
						{loading ? (
							<div className='text-center py-12'>
								<div className='text-gray-500'>Loading posts...</div>
							</div>
						) : posts.length === 0 ? (
							<div className='text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300 px-6'>
								<div className='inline-flex items-center justify-center w-16 h-16 bg-gray-50 rounded-full mb-4'>
									<MapPin className='w-8 h-8 text-gray-400' />
								</div>
								<h3 className='text-lg font-bold text-gray-900 mb-2'>Quiet around here...</h3>
								<p className='text-gray-500 max-w-sm mx-auto'>
									{searchQuery ? (
										<span>
											No posts match "
											<strong>{searchQuery.length > 25 ? `${searchQuery.slice(0, 25)}...` : searchQuery}</strong>" in
											this area. Try a different search term or clear the filter to see more results.
										</span>
									) : radius < 4 ? (
										'No posts found in this range. Try increasing the search radius or moving the map pin to find more activity!'
									) : (
										'Still nothing! It seems there are no recent posts within 500km of this location. Try exploring a different region.'
									)}
								</p>
							</div>
						) : (
							posts.map((post: ExplorePostDataType) => (
								<article
									key={post.id}
									className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow'
									style={{
										contentVisibility: 'auto',
										containIntrinsicSize: 'auto 500px',
									}}
									onClick={() => navigateToPost(post.id.toString())}
								>
									{/* Post Header */}
									<div className='p-2.5 sm:p-4 border-b border-gray-100'>
										{/* Line 1: Author (bigger pfp + name over date) + Risk button,
										    kept vertically centered on this row. */}
										<div className='flex items-center justify-between gap-1.5 sm:gap-3'>
											<div className='flex items-center gap-3 sm:gap-4 min-w-0'>
												<ProfileImage
													name={post.name}
													pfpURL={post.profilepicURL}
													className='w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover border border-gray-200 shrink-0'
													isAnonymous={post.isAnonymous}
												/>
												{/* Name uses the former title size, sitting just above the date.
												    items-start keeps name and date individually left-aligned. */}
												<div className='flex flex-col items-start min-w-0'>
													<span className='font-bold text-gray-900 text-sm sm:text-xl leading-tight truncate text-left'>
														{renderHighlightedText(post.name, searchQuery)}
													</span>
													<div className='flex items-center text-[10px] sm:text-xs text-gray-500 gap-1 sm:gap-1.5'>
														<Clock className='w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 shrink-0' />
														<span className='truncate'>
															{(() => {
																try {
																	const date = parseISO(post.time);
																	const distancePart = formatCustomDistanceToNow(date);
																	return distancePart;
																} catch (e) {
																	return post.time;
																}
															})()}
														</span>
													</div>
												</div>
											</div>
											{!post.youtubeLink && (
												<button
													className='flex items-center gap-1 px-1.5 py-0.5 sm:gap-1.5 sm:px-3 sm:py-1 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors text-[10px] sm:text-sm font-semibold border border-amber-100 shrink-0 self-center'
													onClick={(e) => {
														e.stopPropagation();
														if (post.media_filename) {
															setRiskoMeterMediaId(post.media_filename);
														}
													}}
													title='Risko-Meter'
												>
													<Gauge className='w-3 h-3 sm:w-4 sm:h-4' />
													<span>Risk</span>
												</button>
											)}
										</div>

										{/* Line 2: Title — now uses the former, smaller username size. */}
										<h2 className='text-[10px] sm:text-sm font-semibold text-gray-900 text-left truncate mt-3 sm:mt-4' title={post.title || ''}>
											{renderHighlightedText(post.title, searchQuery)}
										</h2>

										{/* Line 3: Description preview — replaces the tags row visually.
										    Tags are still kept on the post object so filtering works. */}
										{post.content && post.content.trim() !== '' && (
											<CardDescription text={post.content.trim()} />
										)}
									</div>

									{/* Post Media */}
									{post.isImage && (
										<div className='relative'>
											<PostImage
												src={post.image_url}
												alt='Post'
												className='w-full h-auto max-h-96 object-cover bg-gray-100'
											/>
										</div>
									)}

									{/* YouTube/Web Content */}
									{post.youtubeLink && (
										<div className='px-4 my-3' onClick={(e) => e.stopPropagation()}>
											<div className='aspect-video rounded-lg overflow-hidden'>
												<iframe
													src={`https://www.youtube.com/embed/${post.youtubeLink}`}
													className='w-full h-full'
													frameBorder='0'
													title='YouTube video'
													allowFullScreen
													allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure'
												/>
											</div>
										</div>
									)}

									{post.webLink && (
										<div className='px-4 my-3' onClick={(e) => e.stopPropagation()}>
											<div className='aspect-video rounded-lg overflow-hidden border border-gray-200'>
												<iframe
													src={post.webLink}
													className='w-full h-full'
													title={post.title}
													sandbox='allow-scripts allow-same-origin allow-popups allow-forms allow-presentation'
													referrerPolicy='no-referrer'
													allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure'
												/>
											</div>
										</div>
									)}

									{/* Action Buttons (Footer) */}
									<div className='h-8 sm:h-12 shrink-0'>
										<div className='flex items-center justify-between h-full'>
											{/* Like Region */}
											<button
												onClick={(e) => {
													e.stopPropagation();
													handleLikeClick(post.id, post.name === 'Anonymous');
												}}
												className={`flex-1 flex items-center justify-center h-full transition-colors ${(postMetadata[post.id]?.isLiked ?? likes[post.id])
														? 'text-blue-600 bg-blue-50/50 hover:bg-blue-50'
														: 'text-gray-600 hover:bg-gray-50'
													} border-t border-r border-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black`}
											>
												<div className='flex items-center relative'>
													{(postMetadata[post.id]?.likeCount ?? post.likeCount) > 0 && (
														<span className='absolute right-full mr-1 sm:mr-2 font-medium text-[10px] sm:text-sm whitespace-nowrap'>
															{postMetadata[post.id]?.likeCount ?? post.likeCount}
														</span>
													)}
													<Heart
														className={`w-3 h-3 sm:w-5 sm:h-5 transition-all ${(postMetadata[post.id]?.isLiked ?? likes[post.id]) ? 'fill-current scale-110' : 'scale-100'
															}`}
													/>
													<span className='text-[10px] sm:text-sm font-medium ml-1 sm:ml-2'>Like</span>
												</div>
											</button>

											{/* Comment Region */}
											<button
												type='button'
												className='flex-1 flex items-center justify-center h-full text-gray-600 hover:bg-gray-50 border-t border-r border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black'
											>
												<div className='flex items-center relative'>
													{(postMetadata[post.id]?.commentCount ?? post.commentCount) > 0 && (
														<span className='absolute right-full mr-1 sm:mr-2 font-medium text-[10px] sm:text-sm whitespace-nowrap'>
															{postMetadata[post.id]?.commentCount ?? post.commentCount}
														</span>
													)}
													<MessageCircle className='w-3 h-3 sm:w-5 sm:h-5' />
													<span className='text-[10px] sm:text-sm font-medium ml-1 sm:ml-2'>Comment</span>
												</div>
											</button>

											{/* Share Region */}
											<div className='flex-1 relative h-full' ref={openShareId === post.id ? shareDropdownRef : null}>
												<button
													onClick={(e) => {
														e.stopPropagation();
														handleShareButtonClick(post.id);
													}}
													className='w-full h-full flex items-center justify-center space-x-1 sm:space-x-2 text-gray-600 hover:bg-gray-50 border-t border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-black'
												>
													<Share2 className='w-3 h-3 sm:w-5 sm:h-5' />
													<span className='font-medium text-[10px] sm:text-sm'>Share</span>
												</button>
												{openShareId === post.id && (
													<div className='absolute right-0 bottom-full mb-2 z-50' onClick={(e) => e.stopPropagation()}>
														<SharingTab url={`${WEB_BASE_URL}/share/${post.id}/${post.shareHash}`} title={post.title} />
													</div>
												)}
											</div>
										</div>
									</div>
								</article>
							))
						)}

						{/* Infinite scroll sentinel — IntersectionObserver triggers next page when it scrolls into view. */}
						{hasMore && posts.length > 0 && (
							<div ref={loadMoreSentinelRef} className='pt-6 pb-12 flex justify-center'>
								{isFetchingMore ? (
									<div className='flex items-center gap-2 text-gray-500'>
										<div className='w-5 h-5 border-2 border-gray-300 border-t-[#004a99] rounded-full animate-spin'></div>
										<span>Loading more...</span>
									</div>
								) : (
									<button
										onClick={handleLoadMore}
										aria-label='Load more nearby posts'
										className='px-4 py-2 text-xs text-gray-400 hover:text-gray-600 rounded-full border border-gray-200 hover:border-gray-300'
									>
										Load more
									</button>
								)}
							</div>
						)}

						{!hasMore && posts.length > 0 && (
							<div className='py-12 text-center'>
								<div className='inline-block px-4 py-2 bg-gray-50 rounded-full border border-gray-100 text-gray-400 text-sm font-medium'>
									No more posts nearby
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
			{riskoMeterMediaId && <RiskoMeter mediaId={riskoMeterMediaId} onClose={() => setRiskoMeterMediaId(null)} />}

			{/* Floating "Go to Top" Button */}
			<div
				className={`go-to-top-button-wrap ${showScrollToOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-[120px] pointer-events-none'
					}`}
			>
				<button
					onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}
					className='go-to-top-button bg-blue-600 text-white w-12 h-12 rounded-full shadow-2xl flex items-center justify-center hover:bg-blue-700 active:scale-95 transition-all'
					title='Go to Top'
				>
					<ArrowUp size={24} />
				</button>
			</div>
		</div>
	);
};

export default Explore;
